/**
 * Creates a mutation observer.
 *
 * @param {Object} options - The options object.
 * @param {HTMLElement} options.target - The target element.
 * @param {Function} options.handleMutations - The handle mutations function.
 * @returns {MutationObserver} The mutation observer.
 */
// ---------------------------------------------------------------------------
// Transaction Item IDs
// ---------------------------------------------------------------------------

/**
 * Numeric item-id scheme for transaction rows:
 *   itemId = section * ITEM_PART_BASE + table * ITEM_TABLE_BASE + row
 *
 * Each table holds up to ITEM_MAX_ROWS rows without running into the next
 * table, and the largest id (D.9 row 9,999,999) still fits a signed INT.
 *
 * Example: B.2 row 1 → 120000001, B.3 row 1 → 130000001.
 */
export const ITEM_PART_BASE = 100_000_000;
export const ITEM_TABLE_BASE = 10_000_000;
export const ITEM_MAX_ROWS = ITEM_TABLE_BASE - 1;

/** Legacy scheme (section * 1000 + table * 100 + row), kept for decoding only. */
const LEGACY_ITEM_ID_LIMIT = 10_000;

/**
 * Builds the item-id of a transaction row.
 *
 * @param {number} section - 1 (Part B), 2 (Part C), 3 (Part D)
 * @param {number} table - Table number within the section (1-based)
 * @param {number} row - Row number within the table (1-based)
 * @returns {number}
 */
export function itemIdOf(section, table, row) {
    return section * ITEM_PART_BASE + table * ITEM_TABLE_BASE + row;
}

/**
 * Splits an item-id into section, table and row. Also accepts legacy ids
 * (below 10,000) stored before the scheme was widened.
 *
 * @param {number|string} itemId
 * @returns {{section: number, table: number, row: number}}
 */
export function decodeItemId(itemId) {
    const id = parseInt(itemId, 10);
    if (id < LEGACY_ITEM_ID_LIMIT) {
        return {
            section: Math.floor(id / 1000),
            table: Math.floor((id % 1000) / 100),
            row: id % 100,
        };
    }
    return {
        section: Math.floor(id / ITEM_PART_BASE),
        table: Math.floor((id % ITEM_PART_BASE) / ITEM_TABLE_BASE),
        row: id % ITEM_TABLE_BASE,
    };
}

/**
 * Re-encodes a stored item-id (legacy or current) in the current scheme, so
 * it matches the item-id attributes rendered on the form.
 *
 * @param {number|string} itemId
 * @returns {number}
 */
export function toCurrentItemId(itemId) {
    const { section, table, row } = decodeItemId(itemId);
    return itemIdOf(section, table, row);
}

/**
 * Transforms transaction keys by adding a section.table prefix and
 * removing any parenthetical text from labels.
 *
 * Prefix is derived from the numeric item ID (see `decodeItemId`):
 *   section → 1 (Part B), 2 (Part C), 3 (Part D)
 *   table   → 1, 2, 3, 4
 *   prefix  = "section.table: "
 *
 * Example: itemId 120000001, label "Total Price (excluding fees)"
 *   → "B.2: Total Price"
 *
 * @param {Object} transactions - Flat transactions { itemId: { label: value } }
 * @returns {Object} Transformed transactions with prefixed, cleaned keys
 */
export function transformTransactionKeys(transactions) {
    const result = {};
    const sectionPrefix = ["B", "C", "D"];

    Object.entries(transactions).forEach(([itemId, row]) => {
        const { section, table } = decodeItemId(itemId);
        const prefix = `${sectionPrefix[section - 1]}.${table}`;

        const transformedRow = {};
        Object.entries(row).forEach(([label, value]) => {
            const cleaned = label.replace(/\s*\([^)]*\)/g, "").trim();
            transformedRow[`${prefix}: ${cleaned}`] = value;
        });

        result[itemId] = transformedRow;
    });

    return result;
}

// ---------------------------------------------------------------------------
// Thousands Separator Formatting
// ---------------------------------------------------------------------------

/**
 * Formats a numeric value string with thousands separators (commas).
 * Handles both integers and decimals.
 *
 * @param {string|number} value - The numeric value to format.
 * @returns {string} Formatted string, e.g. "431,149.54" or "19,815"
 */
export function formatWithThousandsSeparator(value) {
    if (value === "" || value == null) return "";
    const str = String(value).replace(/,/g, "");
    if (isNaN(parseFloat(str))) return String(value);
    const parts = str.split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.join(".");
}

/**
 * Strips thousands separators (commas) from a string.
 *
 * @param {string} value
 * @returns {string}
 */
export function stripThousandsSeparator(value) {
    return String(value).replace(/,/g, "");
}

/**
 * Formats the value of a numeric text input with thousands separators
 * in real-time, preserving the cursor position.
 */
function onInputFormatWithCommas(event) {
    const input = event.target;
    const raw = input.value;

    if (raw === "") return;

    let stripped = raw.replace(/,/g, "");

    if (!/^\d*\.?\d*$/.test(stripped)) return;

    const maxDecimals = 4;
    const dotIndex = stripped.indexOf(".");

    if (maxDecimals === 0 && dotIndex !== -1) {
        stripped = stripped.substring(0, dotIndex);
    } else if (
        dotIndex !== -1 &&
        stripped.length - dotIndex - 1 > maxDecimals
    ) {
        stripped = stripped.substring(0, dotIndex + maxDecimals + 1);
    }

    const cursorPos = input.selectionStart ?? raw.length;
    const charsBeforeCursor = raw
        .substring(0, cursorPos)
        .replace(/,/g, "").length;

    const parts = stripped.split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    const formatted = parts.join(".");

    if (formatted !== raw) {
        input.value = formatted;

        let newPos = 0;
        let count = 0;
        for (
            let i = 0;
            i < formatted.length && count < charsBeforeCursor;
            i++
        ) {
            newPos = i + 1;
            if (formatted[i] !== ",") count++;
        }
        input.setSelectionRange(newPos, newPos);
    }
}

/**
 * Converts a number input to a text input with thousands separator formatting.
 * Commas are inserted in real-time as the user types.
 *
 * @param {HTMLInputElement} input
 */
export function enableThousandsSeparator(input) {
    const step = input.getAttribute("step") || "";
    const min = input.getAttribute("min") || "";

    input.setAttribute("type", "text");
    input.setAttribute("inputmode", "decimal");
    if (step) input.setAttribute("data-step", step);
    if (min) input.setAttribute("data-min", min);

    input.addEventListener("input", onInputFormatWithCommas);
}

/**
 * Removes thousands separator formatting listeners from an input.
 *
 * @param {HTMLInputElement} input
 */
export function disableThousandsSeparator(input) {
    input.removeEventListener("input", onInputFormatWithCommas);
}

/**
 * Formats existing values in all enabled inputs that already have data.
 * Useful after programmatically loading values (e.g. editing an existing claim).
 *
 * @param {HTMLElement} container
 */
export function formatExistingValues(container) {
    container
        .querySelectorAll('.transactions input[inputmode="decimal"]')
        .forEach((input) => {
            if (input.value) {
                onInputFormatWithCommas({ target: input });
            }
        });
}

export function createMutationObserver({ target, handleMutations }) {
    const observer = new MutationObserver((mutations) => {
        handleMutations(mutations);
    });

    observer.observe(target, { childList: true, subtree: true });

    return observer;
}
